import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate } from "@/lib/format";
import { friendlyError } from "@/lib/askThemes";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STEPS = [
  ["requested", "Request shared"],
  ["accepted", "Lender agreed"],
  ["sent", "Lender says money sent"],
  ["recorded", "You confirmed — debt recorded"],
];
const ORDER = { pending: 0, countered: 0, accepted: 1, sent: 2, recorded: 3 };

export default function LendOwnerPanel({ a, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const l = a.lend;
  const act = async (path) => {
    setBusy(true);
    try { onChange((await api.post(`/asks/${a.id}/${path}`)).data); } catch (e) {
      const x = apiError(e);
      if (x === "free_limit_reached") setLimitOpen(true); else toast.error(friendlyError(x));
    } finally { setBusy(false); }
  };
  const at = ORDER[a.status] ?? -1;
  return (
    <div data-testid="lend-owner-panel" className="rounded-2xl border border-border bg-card p-4 space-y-4">
      {a.status === "declined" ? (
        <p data-testid="lend-declined" className="text-sm">{l.lender_name || "Your contact"} declined this request.{l.decline_note ? ` “${l.decline_note}”` : ""}</p>
      ) : (
        <ol className="space-y-2.5">
          {STEPS.map(([k, label], i) => (
            <li key={k} className={`flex items-center gap-2.5 text-sm ${i <= at ? "font-semibold" : "text-muted-foreground"}`}>
              {i <= at ? <CheckCircle2 className="h-4 w-4 text-accent" /> : <Circle className="h-4 w-4" />}{label}
            </li>
          ))}
        </ol>
      )}

      {a.status === "countered" && l.counter && (
        <div data-testid="lend-counter" className="rounded-xl bg-amber-50 p-3 text-sm">
          <p className="font-semibold text-amber-900">{l.lender_name || "Your contact"} proposed new terms</p>
          <p className="mt-1">{fmtNaira(l.counter.amount, a.currency)} · repay by {fmtDate(l.counter.repay_date)}</p>
          {l.counter.note && <p className="mt-1 text-xs text-muted-foreground">“{l.counter.note}”</p>}
          <div className="mt-3 flex gap-2">
            <Button data-testid="lend-counter-accept" disabled={busy} onClick={() => act("counter/accept")} className="h-10 flex-1 rounded-xl">Accept terms</Button>
            <Button data-testid="lend-counter-decline" disabled={busy} variant="outline" onClick={() => act("counter/decline")} className="h-10 flex-1 rounded-xl">Decline</Button>
          </div>
        </div>
      )}

      {a.status === "sent" && (
        <div data-testid="lend-confirm-receipt" className="rounded-xl bg-emerald-50 p-3">
          <p className="text-sm font-semibold">{l.lender_name || "Your lender"} says they sent {fmtNaira(l.amount, a.currency)}.</p>
          <p className="mt-1 text-xs text-muted-foreground">Check your bank app. Confirming records a debt of {fmtNaira(l.amount, a.currency)} due {fmtDate(l.repay_date)} — terms can't be changed here.</p>
          <div className="mt-3 flex gap-2">
            <Button data-testid="lend-received-button" disabled={busy} onClick={() => act("received")} className="h-10 flex-1 rounded-xl">Received</Button>
            <Button data-testid="lend-not-received-button" disabled={busy} variant="outline" onClick={() => act("not-received")} className="h-10 flex-1 rounded-xl">Not yet</Button>
          </div>
        </div>
      )}

      {a.status === "recorded" && l.debt_id && (
        <button data-testid="lend-view-debt" onClick={() => navigate(`/debt/${l.debt_id}`)} className="flex w-full items-center justify-between rounded-xl bg-secondary px-3 py-3 text-sm font-semibold">
          View the debt in Debii <ArrowRight className="h-4 w-4" />
        </button>
      )}

      <AlertDialog open={limitOpen} onOpenChange={setLimitOpen}>
        <AlertDialogContent data-testid="lend-limit-dialog" className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Free plan limit reached</AlertDialogTitle>
            <AlertDialogDescription>You already have 5 active debts. Settle one or upgrade to Debii Plus to record this loan. Your request stays safe in the meantime.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Not now</AlertDialogCancel>
            <AlertDialogAction data-testid="lend-limit-upgrade" onClick={() => navigate("/upgrade")} className="rounded-xl">Upgrade</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
